import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useQuery } from '@apollo/client';

import { getAmountPrefixAndColor } from '@libs/helpers';
import { CurrencySymbol, HistoryPeriodEnumKeys } from '@libs/types';

import { GetUserPnlArgs, getUserPnlQuery } from 'src/gql';

import { useApiResponse } from './useApiResponse';

type UseUserPnlProps = GetUserPnlArgs & {
  currencySymbol?: CurrencySymbol;
};
export const useUserPnl = ({
  range,
  assetCode,
  valueAssetCode,
  currencySymbol,
}: UseUserPnlProps) => {
  const { colors } = useTheme();

  const res = useQuery(getUserPnlQuery, {
    variables: {
      record: {
        range,
        valueAssetCode,
        assetCode,
      },
    },
    fetchPolicy: 'network-only',
  });
  const { response } = useApiResponse(res);
  const { prefix, absoluteAmount, absolutePercentage, hasShrunk } =
    getAmountPrefixAndColor(
      colors,
      response?.getUserPNL.amount,
      response?.getUserPNL.percentage
    );

  const changeColor =
    (response?.getUserPNL.amount || 0) < 0
      ? colors.text.critical
      : colors.text.success;

  const defaultPrefix = '≈';

  const oneDayProfitLossText = useMemo(() => {
    if (range !== HistoryPeriodEnumKeys.ONE_DAY) return '';

    const change = response?.getUserPNL.amount;
    if (change === undefined) return '';

    return Number(change) >= 0
      ? 'pnl_current_day_profit'
      : 'pnl_current_day_loss';
  }, [range, response]);

  return {
    prefix,
    changeColor,
    assembledText: `${defaultPrefix} ${absoluteAmount}${currencySymbol} (${prefix}${absolutePercentage}%)`,
    absoluteAmount,
    absolutePercentage,
    hasShrunk,
    oneDayProfitLossText,
  };
};
